import Cookies from 'js-cookie';

import {
	LOGIN_REQUEST,
	LOGIN_SUCCESS,
	LOGIN_FAILURE,
	CLEAR_LOGIN_ERROR
} from '../../constants';

import {
	pharmacy_login_2fa_check,
	pharmacy_login_2fa_email_send,
	pharmacy_login_2fa_email_verify,
	pharmacy_login_2fa_phone_send,
	pharmacy_login_2fa_phone_verify,
	pharmacy_login_2fa_authenticator_setup,
	pharmacy_login_2fa_authenticator_confirm,
	pharmacy_login_2fa_authenticator_verify,
	pharmacy_login_2fa_ip_authorization_verify
} from '../../../networking/urlEndPoints';

import Axios from '../../../networking/intraceptor';
import * as routeNames from '../../../routes/routeName';
import isMobileDevice from '../../../utils/isMobileDevice';
import tabId from '../../../utils/helper/tabID';
import { fetchUserProfile, fetchPharmacyFileManagerUrl, getPharmacyAccountabilitySettings, getAfterProcessPrepCheckSetting } from './userProfileActions';
import { authPharmacyPermissions } from './authPermissions';
import { fetchTimesheetDates } from '../main/pharmacyTimesheetDatesActions';
import { fetchProvinceList } from '../patients/provinceListsActions';
import { addUserActivityStatus } from '../chat/activityStatusActions';
import { createAction, actionTypes, CheckBookkeeperModuleAccess, CheckLogsExists } from '../../reducers/pharmacy/other/commonReducerForInitalData';
import { primeMyModules } from '../../../hooks/useMyModules';
import { primeAdminStealth } from '../../../hooks/useAdminStealth';

export const twoFactorLoginRequest = () => ({
	type: LOGIN_REQUEST,
});

export const twoFactorLoginSuccess = (user) => ({
	type: LOGIN_SUCCESS,
	payload: user,
});

export const twoFactorLoginFailure = (error) => ({
	type: LOGIN_FAILURE,
	payload: error,
});

export const clearTwoFactorLoginError = () => ({
	type: CLEAR_LOGIN_ERROR,
});

const extractErrorPayload = (error) => {
	if (error?.response) {
		return error.response;
	}

	return {
		status: 0,
		data: {
			message: error?.message || 'Unable to reach the backend. Please try again.'
		}
	};
};

const handleSuccessfulTwoFactorLogin = async (dispatch, response, navigate) => {
	Cookies.set('authToken', response?.data?.data?.token);
	// This function is reached only after the backend has issued the final
	// portal token. Resolve the module list before navigating so the header does
	// not render old permissions and then change. It never runs for an OTP
	// response that still requires IP authorization.
	await primeMyModules();
	// Resolve admin screen privacy (F9 x5) before the portal renders.
	await primeAdminStealth();
	const loginPharmacyData = response?.data?.data?.pharmacy?.[0];
	const settingsData = response?.data?.data?.settings;
	const user = response?.data?.data;

	dispatch(twoFactorLoginSuccess(user));
	dispatch(authPharmacyPermissions());
	dispatch(fetchUserProfile());
	dispatch(fetchPharmacyFileManagerUrl());
	dispatch(fetchTimesheetDates());
	dispatch(fetchProvinceList());
	dispatch(addUserActivityStatus('online'));
	dispatch(getPharmacyAccountabilitySettings(loginPharmacyData?.finalchk_at));
	dispatch(getAfterProcessPrepCheckSetting(settingsData?.pre_after_process ?? false));
	dispatch(createAction(actionTypes.UPDATE_LABEL_TO_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_top_margin,
		requestType: 'updateLabelToMargin'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_HEADER.SUCCESS, {
		data: !!loginPharmacyData?.label_print_header,
		requestType: 'updateLabelPrintHeader'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_HEADER_TOP_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_header_top_margin ?? 0,
		requestType: 'updateLabelHeaderTopMargin'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_TYPE.SUCCESS, {
		data: loginPharmacyData?.label_print_type || 'regular',
		requestType: 'updateLabelPrintType'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_LOGO.SUCCESS, {
		data: !!loginPharmacyData?.label_print_logo,
		requestType: 'updateLabelPrintLogo'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_LEFT_MARGIN.SUCCESS, {
		data: loginPharmacyData?.label_left_margin ?? 0,
		requestType: 'updateLabelLeftMargin'
	}));
	dispatch(createAction(actionTypes.UPDATE_LABEL_SIG_GAP.SUCCESS, {
		data: loginPharmacyData?.label_sig_gap ?? 0,
		requestType: 'updateLabelSigGap'
	}));
	dispatch(CheckBookkeeperModuleAccess({ fetchType: 'checkBookkeeperModuleAccess' }));
	dispatch(CheckLogsExists({ fetchType: 'checkLogsExists' }));

	if (typeof BroadcastChannel !== 'undefined') {
		const channel = new BroadcastChannel('auth-channel');
		channel.postMessage({
			type: 'login',
			tabId,
			response: user,
			loginPharmacyData,
			settingsData
		});
		channel.close();
	}

	const redirectPath = sessionStorage.getItem('postLoginRedirectPath');

	if (redirectPath === routeNames.pharmacy_accountability_upload_pics) {
		navigate(isMobileDevice() ? redirectPath : routeNames.DASHBOARD_ROUTE);
	} else if (redirectPath) {
		navigate(redirectPath);
	} else {
		navigate(routeNames.DASHBOARD_ROUTE);
	}
};

const postTwoFactor = (endpoint, payload, navigate, shouldCompleteLogin = false) => {
	return async (dispatch) => {
		dispatch(twoFactorLoginRequest());

		try {
			const response = await Axios.post(endpoint, payload);

			const loginData = response?.data?.data || {};
			// IP authorization is another required login step. Even if a future
			// backend response accidentally includes a token at that stage, keep
			// the user on the OTP screen until IP authorization is complete.
			const isFinalLogin = shouldCompleteLogin
				&& Boolean(loginData.token)
				&& loginData.ip_authorization_required !== true;

			if (isFinalLogin) {
				await handleSuccessfulTwoFactorLogin(dispatch, response, navigate);
				return {
					completed: true,
					data: loginData
				};
			}

			dispatch(twoFactorLoginSuccess(response?.data?.data));
			return {
				completed: false,
				data: response?.data?.data,
				message: response?.data?.message
			};
		} catch (error) {
			const errorPayload = extractErrorPayload(error);
			dispatch(twoFactorLoginFailure(errorPayload));
			return {
				completed: false,
				error: errorPayload,
				message: errorPayload?.data?.message || 'Unable to process login verification.'
			};
		}
	};
};

export const checkPharmacyLoginCredentials = (email, password) => postTwoFactor(
	pharmacy_login_2fa_check,
	{ email, password }
);

export const sendPharmacyLoginEmailOtp = (challengeToken, email = null) => postTwoFactor(
	pharmacy_login_2fa_email_send,
	email ? { challenge_token: challengeToken, email } : { challenge_token: challengeToken }
);

export const verifyPharmacyLoginEmailOtp = (challengeToken, otp, navigate) => postTwoFactor(
	pharmacy_login_2fa_email_verify,
	{ challenge_token: challengeToken, otp },
	navigate,
	true
);

export const sendPharmacyLoginPhoneOtp = (challengeToken) => postTwoFactor(
	pharmacy_login_2fa_phone_send,
	{ challenge_token: challengeToken }
);

export const verifyPharmacyLoginPhoneOtp = (challengeToken, otp, navigate) => postTwoFactor(
	pharmacy_login_2fa_phone_verify,
	{ challenge_token: challengeToken, otp },
	navigate,
	true
);

export const setupPharmacyLoginAuthenticator = (challengeToken) => postTwoFactor(
	pharmacy_login_2fa_authenticator_setup,
	{ challenge_token: challengeToken }
);

export const confirmPharmacyLoginAuthenticator = (challengeToken, code, navigate) => postTwoFactor(
	pharmacy_login_2fa_authenticator_confirm,
	{ challenge_token: challengeToken, code },
	navigate,
	true
);

export const verifyPharmacyLoginAuthenticator = (challengeToken, code, navigate) => postTwoFactor(
	pharmacy_login_2fa_authenticator_verify,
	{ challenge_token: challengeToken, code },
	navigate,
	true
);

export const verifyPharmacyLoginIpAuthorizationOtp = (challengeToken, otp, navigate) => postTwoFactor(
	pharmacy_login_2fa_ip_authorization_verify,
	{ challenge_token: challengeToken, otp },
	navigate,
	true
);
